import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  Link as MuiLink,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Collapse,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Comment as CommentIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { getWSRItems, getWSRCurrentQuarterItems, addWSRItem, removeWSRItem, moveToWeeklyReport, removeFromWeeklyReport, updateWSRItem, getWSRItemDetails, getWSRItemBugs, getWSRItemTestCases, refreshWSRItem, generateWSRReport, listWSRReports, getWSRReportLog, deleteWSRReport, getTeamsWithStreams, getComments, addComment } from '../../services/apiService';
import { getCurrentUser, hasRole } from '../../services/authService';
import { formatDate, formatDateTime } from '../../utils/formatters';

/** Section keys and labels for WSR comment sections */
const WSR_COMMENT_SECTION_KEYS = ['escalationForHelp', 'risks', 'weeksProgress', 'dependencies', 'devOpsReadiness'];
const WSR_COMMENT_SECTION_LABELS = {
  escalationForHelp: 'Escalation for Help',
  risks: 'Risks',
  weeksProgress: "Week's progress",
  dependencies: 'Dependencies',
  devOpsReadiness: 'DevOps work / Readiness'
};
const defaultAddCommentSections = () =>
  Object.fromEntries(WSR_COMMENT_SECTION_KEYS.map((k) => [k, { text: '', notApplicable: false }]));

/** Status filter options with display order and colors */
const WSR_STATUS_FILTER_OPTIONS = ['Green', 'Amber', 'Red'];

/** Work item types in the type filter (ADO names); labels use WSR_WORK_ITEM_TYPE_LABEL */
const WSR_TYPE_FILTER_TYPES = ['Epic', 'Feature', 'Product Backlog Item'];
const WSR_WORK_ITEM_TYPE_LABEL = {
  Epic: 'Epic',
  Feature: 'Feature',
  'Product Backlog Item': 'PBI'
};
const WSR_TYPE_FILTER_ORDER = { Epic: 0, Feature: 1, 'Product Backlog Item': 2 };

/**
 * Bulk GET /api/wsr/:id/details on WSR load. Set REACT_APP_WSR_AUTO_FETCH_ITEM_DETAILS=false in .env.development to skip (local dev).
 * Default true. Requires dev server restart after changing .env.
 */
const WSR_BULK_DETAILS_FETCH_ENABLED = (() => {
  const v = (process.env.REACT_APP_WSR_AUTO_FETCH_ITEM_DETAILS || '').trim().toLowerCase();
  if (v === 'false' || v === '0' || v === 'no' || v === 'off') return false;
  return true;
})();

/**
 * @param {object} details - wsrItemDetails entry
 * @param {{ selectedWorkItemTypes: string[], targetDateBefore: string, excludeCompleted: boolean }} opts
 */
function wsrItemPassesTypeTargetCompletedFilters(details, opts) {
  const { selectedWorkItemTypes, targetDateBefore, excludeCompleted } = opts;
  if (selectedWorkItemTypes?.length > 0) {
    const wit = details?.workItemType || '';
    if (!selectedWorkItemTypes.includes(wit)) return false;
  }
  if (targetDateBefore) {
    const td = details?.targetDate;
    if (td == null || td === '') return false;
    const itemMs = new Date(td).setHours(0, 0, 0, 0);
    const cutoffMs = new Date(`${targetDateBefore}T12:00:00`).setHours(0, 0, 0, 0);
    if (itemMs >= cutoffMs) return false;
  }
  if (excludeCompleted) {
    const state = String(details?.state || '').trim().toLowerCase();
    if (state === 'done' || state === 'closed') return false;
    const pct = Number(details?.completionPercentage);
    if (!Number.isNaN(pct) && pct >= 100) return false;
  }
  return true;
}

/**
 * WSRView component for displaying Weekly Status Report items
 * @returns {JSX.Element} WSRView component
 */
const WSRView = () => {
  const [wsrItems, setWsrItems] = useState([]);
  const [wsrItemDetails, setWsrItemDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAdoId, setNewAdoId] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshingAdoId, setRefreshingAdoId] = useState(null);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listDialogType, setListDialogType] = useState(null); // 'bugs' | 'test-cases'
  const [listDialogAdoId, setListDialogAdoId] = useState(null);
  const [listDialogTitle, setListDialogTitle] = useState('');
  const [listDialogItems, setListDialogItems] = useState([]);
  const [listDialogLoading, setListDialogLoading] = useState(false);
  const [canAddItems, setCanAddItems] = useState(false);
  const [canGenerateReport, setCanGenerateReport] = useState(false);
  const [currentQuarterItems, setCurrentQuarterItems] = useState([]); // Epics/Features from EpicHierarchy for current quarter (displayed in stream tabs)
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedWorkItemTypes, setSelectedWorkItemTypes] = useState([]);
  const [targetDateBefore, setTargetDateBefore] = useState('');
  const [excludeCompleted, setExcludeCompleted] = useState(false);
  const [teamsWithStreams, setTeamsWithStreams] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0); // 0 = Weekly Report (first tab), 1+ = stream tabs
  const [expandedEpics, setExpandedEpics] = useState(new Set());
  const [movingToReportAdoId, setMovingToReportAdoId] = useState(null);
  const [removingFromReportAdoId, setRemovingFromReportAdoId] = useState(null);
  const [statusUpdatingAdoId, setStatusUpdatingAdoId] = useState(null);
  // Add comment dialog (5 sections with Not Applicable)
  const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false);
  const [addCommentAdoId, setAddCommentAdoId] = useState(null);
  const [addCommentTitle, setAddCommentTitle] = useState('');
  const [addCommentSections, setAddCommentSections] = useState(defaultAddCommentSections());
  const [submittingCommentAdoId, setSubmittingCommentAdoId] = useState(null);
  // View comments dialog
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);
  const [viewCommentsAdoId, setViewCommentsAdoId] = useState(null);
  const [viewCommentsTitle, setViewCommentsTitle] = useState('');
  const [viewCommentsList, setViewCommentsList] = useState([]);
  const [viewCommentsLoading, setViewCommentsLoading] = useState(false);
  // Expandable comment row in Weekly Report table (last comment in tabbed view)
  const [expandedCommentAdoId, setExpandedCommentAdoId] = useState(null);
  const [rowCommentsCache, setRowCommentsCache] = useState({});
  const [rowCommentsLoadingAdoId, setRowCommentsLoadingAdoId] = useState(null);
  const [commentRowTabValue, setCommentRowTabValue] = useState(0); // 0 = Last comment, 1 = All comments
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportList, setReportList] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [deletingReportFilename, setDeletingReportFilename] = useState(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [logDialogContent, setLogDialogContent] = useState('');
  const [logDialogLoading, setLogDialogLoading] = useState(false);
  const [logDialogTitle, setLogDialogTitle] = useState('');
  // Sort state for Weekly Report table (default: Status then Team)
  const [wsrSortBy, setWsrSortBy] = useState('status');
  const [wsrSortOrder, setWsrSortOrder] = useState('asc');
  // PRD/Design updating (per row) to avoid double-trigger
  const [prdDesignUpdatingAdoId, setPrdDesignUpdatingAdoId] = useState(null);

  // Check if user can add items (Admin, Dev Manager, or QA Manager)
  useEffect(() => {
    const user = getCurrentUser();
    const canAdd = user && (
      hasRole('admin') ||
      hasRole('dev_manager') ||
      hasRole('qa_manager')
    );
    setCanAddItems(canAdd);
  }, []);

  // Generate WSR report is Admin only
  useEffect(() => {
    setCanGenerateReport(Boolean(getCurrentUser() && hasRole('admin')));
  }, []);

  /**
   * Fetch WSR list + quarter items. Optionally bulk-fetch details (see REACT_APP_WSR_AUTO_FETCH_ITEM_DETAILS).
   * @param {{ loadDetails?: boolean }} options - pass loadDetails: false to sync lists only (no bulk /details calls)
   */
  const fetchWSRItems = async (options = {}) => {
    const loadDetails =
      typeof options.loadDetails === 'boolean' ? options.loadDetails : WSR_BULK_DETAILS_FETCH_ENABLED;
    try {
      setLoading(true);
      setError(null);
      const [wsrResponse, quarterResponse] = await Promise.all([
        getWSRItems(),
        getWSRCurrentQuarterItems().catch(() => ({ data: [], quarter: null }))
      ]);
      const items = wsrResponse.data || wsrResponse || [];
      setWsrItems(items);

      const quarterData = quarterResponse?.data || [];
      setCurrentQuarterItems(quarterData);

      // Items to show in stream tabs: current quarter list merged with WSR state (inWeeklyReport, status)
      const streamDisplayList = quarterData.length > 0
        ? quarterData.map(({ adoId }) => {
            const saved = items.find(w => String(w.adoId) === String(adoId));
            return { adoId, ...(saved || {}) };
          })
        : items;

      const allAdoIds = [...new Set([
        ...streamDisplayList.map(i => i.adoId),
        ...items.map(w => w.adoId)
      ])];

      if (loadDetails && allAdoIds.length > 0) {
        const detailsPromises = allAdoIds.map(async (adoId) => {
          try {
            const detailsResponse = await getWSRItemDetails(adoId);
            return {
              adoId,
              details: detailsResponse.data || detailsResponse
            };
          } catch (err) {
            console.error(`Error fetching details for ${adoId}:`, err);
            return { adoId, details: null };
          }
        });

        const detailsResults = await Promise.all(detailsPromises);
        const detailsMap = {};
        detailsResults.forEach(({ adoId, details }) => {
          detailsMap[adoId] = details;
        });
        setWsrItemDetails(detailsMap);
      }
    } catch (err) {
      console.error('Error fetching WSR items:', err);
      setError('Failed to load WSR items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWSRItems();
    fetchTeamsWithStreams();
  }, []);

  // Fetch teams with streams
  const fetchTeamsWithStreams = async () => {
    try {
      const teams = await getTeamsWithStreams();
      setTeamsWithStreams(teams);
    } catch (err) {
      console.error('Error fetching teams with streams:', err);
    }
  };

  // Items to show in stream tabs: from EpicHierarchy current quarter (if any), merged with WSR saved state
  const itemsForStreamTabs = useMemo(() => {
    if (currentQuarterItems.length > 0) {
      return currentQuarterItems.map(({ adoId }) => {
        const saved = wsrItems.find(w => String(w.adoId) === String(adoId));
        return { adoId, ...(saved || {}) };
      });
    }
    return wsrItems;
  }, [currentQuarterItems, wsrItems]);

  // Handle add ADO item – update local state and fetch details for new item only (no full refresh)
  const handleAddItem = async () => {
    if (!newAdoId.trim()) {
      setError('Please enter an ADO ID');
      return;
    }
    const adoId = newAdoId.trim();
    try {
      setAdding(true);
      setError(null);
      const res = await addWSRItem(adoId);
      const newItem = res?.data ?? res;
      setNewAdoId('');
      setAddDialogOpen(false);
      if (newItem && newItem.adoId) {
        setWsrItems((prev) => [...prev, { ...newItem, adoId: String(newItem.adoId) }]);
        try {
          const detailsResponse = await getWSRItemDetails(adoId);
          const details = detailsResponse?.data ?? detailsResponse;
          setWsrItemDetails((prev) => ({ ...prev, [adoId]: details }));
        } catch (e) {
          console.error('Error fetching details for new item:', e);
        }
      }
    } catch (err) {
      console.error('Error adding WSR item:', err);
      setError(err.message || 'Failed to add ADO item. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  // Handle remove ADO item (full remove from WSR) – update local state only
  const handleRemoveItem = async (adoId) => {
    if (!window.confirm(`Are you sure you want to remove ADO item ${adoId} from WSR?`)) {
      return;
    }
    try {
      await removeWSRItem(adoId);
      setWsrItems((prev) => prev.filter((w) => String(w.adoId) !== String(adoId)));
      setWsrItemDetails((prev) => {
        const next = { ...prev };
        delete next[adoId];
        return next;
      });
    } catch (err) {
      console.error('Error removing WSR item:', err);
      setError(err.message || 'Failed to remove ADO item. Please try again.');
    }
  };

  // Move ADO item to Weekly Report tab – update local state only (no full refresh)
  const handleMoveToWeeklyReport = async (adoId) => {
    try {
      setMovingToReportAdoId(adoId);
      setError(null);
      const inWsr = wsrItems.some(w => String(w.adoId) === String(adoId));
      if (!inWsr) {
        const res = await addWSRItem(adoId);
        const newItem = res?.data ?? res;
        if (newItem && newItem.adoId) {
          setWsrItems((prev) => [...prev, { ...newItem, adoId: String(newItem.adoId), inWeeklyReport: true }]);
          try {
            const detailsResponse = await getWSRItemDetails(adoId);
            const details = detailsResponse?.data ?? detailsResponse;
            setWsrItemDetails((prev) => ({ ...prev, [adoId]: details }));
          } catch (e) {
            console.error('Error fetching details for new item:', e);
          }
        }
      } else {
        setWsrItems((prev) =>
          prev.map((w) => (String(w.adoId) === String(adoId) ? { ...w, inWeeklyReport: true } : w))
        );
      }
      await moveToWeeklyReport(adoId);
    } catch (err) {
      console.error('Error moving to weekly report:', err);
      setError(err?.message || 'Failed to add to Weekly Report.');
    } finally {
      setMovingToReportAdoId(null);
    }
  };

  // Remove ADO item from Weekly Report tab only – update local state only
  const handleRemoveFromWeeklyReport = async (adoId) => {
    if (!window.confirm(`Remove ${adoId} from Weekly Report? It will remain in the stream view.`)) {
      return;
    }
    try {
      setRemovingFromReportAdoId(adoId);
      setError(null);
      await removeFromWeeklyReport(adoId);
      setWsrItems((prev) =>
        prev.map((w) => (String(w.adoId) === String(adoId) ? { ...w, inWeeklyReport: false } : w))
      );
    } catch (err) {
      console.error('Error removing from weekly report:', err);
      setError(err?.message || 'Failed to remove from Weekly Report.');
    } finally {
      setRemovingFromReportAdoId(null);
    }
  };

  // Get status for an ADO from wsrItems
  const getStatusForAdo = (adoId) => {
    const item = wsrItems.find(i => String(i.adoId) === String(adoId));
    return item?.status || '';
  };

  const handleStatusChange = async (adoId, status) => {
    const newStatus = status || null;
    // Optimistic update so the selection reflects immediately
    setWsrItems((prev) =>
      prev.map((item) =>
        String(item.adoId) === String(adoId) ? { ...item, status: newStatus } : item
      )
    );
    setStatusUpdatingAdoId(adoId);
    setError(null);
    try {
      await updateWSRItem(adoId, { status: newStatus });
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err?.message || 'Failed to update status.');
      // Revert on failure by refetching
      await fetchWSRItems();
    } finally {
      setStatusUpdatingAdoId(null);
    }
  };

  const handlePrdDesignChange = async (adoId, field, value) => {
    setPrdDesignUpdatingAdoId(adoId);
    setError(null);
    setWsrItems((prev) =>
      prev.map((item) =>
        String(item.adoId) === String(adoId) ? { ...item, [field]: value } : item
      )
    );
    try {
      await updateWSRItem(adoId, { [field]: value });
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setError(err?.message || `Failed to update ${field}.`);
      await fetchWSRItems();
    } finally {
      setPrdDesignUpdatingAdoId(null);
    }
  };

  const openAddCommentDialog = (adoId, title) => {
    setAddCommentAdoId(adoId);
    setAddCommentTitle(title || adoId);
    setAddCommentSections(defaultAddCommentSections());
    setAddCommentDialogOpen(true);
  };

  const closeAddCommentDialog = () => {
    setAddCommentDialogOpen(false);
    setAddCommentAdoId(null);
    setAddCommentTitle('');
    setAddCommentSections(defaultAddCommentSections());
  };

  const hasCommentSectionsContent = (sections) => {
    return WSR_COMMENT_SECTION_KEYS.some(
      (k) => (sections[k]?.text || '').trim() || sections[k]?.notApplicable
    );
  };

  /** Render comment body: structured sections or legacy content */
  const renderCommentBody = (c) => {
    const sections = c.sections && typeof c.sections === 'object';
    const hasAnySection = sections && WSR_COMMENT_SECTION_KEYS.some((k) => c.sections[k]);
    if (hasAnySection) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {WSR_COMMENT_SECTION_KEYS.map((key) => {
            const sec = c.sections[key];
            if (!sec) return null;
            const label = WSR_COMMENT_SECTION_LABELS[key];
            const displayText = sec.notApplicable ? 'Not Applicable' : (sec.text || '').trim() || '—';
            return (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
                  {displayText}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {c.content || '—'}
      </Typography>
    );
  };

  const handleAddCommentFromDialog = async () => {
    if (!addCommentAdoId || !hasCommentSectionsContent(addCommentSections)) return;
    try {
      setSubmittingCommentAdoId(addCommentAdoId);
      setError(null);
      // Send sections and a fallback content (first non-empty section text) so server always has content or sections
      const firstSectionText = WSR_COMMENT_SECTION_KEYS.map((k) => addCommentSections[k]?.text?.trim()).find(Boolean);
      const currentUser = getCurrentUser();
      const displayName = currentUser?.displayName || currentUser?.name || currentUser?.username || (currentUser?.email && currentUser.email.split('@')[0]);
      const payload = {
        sections: addCommentSections,
        content: firstSectionText || ' '
      };
      if (displayName) payload.user = displayName;
      if (currentUser?.email) payload.userEmail = currentUser.email;
      await addComment(addCommentAdoId, payload);
      closeAddCommentDialog();
      if (viewCommentsDialogOpen && viewCommentsAdoId === addCommentAdoId) {
        const res = await getComments(addCommentAdoId);
        const data = res?.data ?? res ?? [];
        setViewCommentsList(Array.isArray(data) ? data : []);
      }
      // Refresh row comment cache so expanded row shows new comment
      if (rowCommentsCache[addCommentAdoId] !== undefined) {
        const res = await getComments(addCommentAdoId);
        const data = res?.data ?? res ?? [];
        setRowCommentsCache((prev) => ({ ...prev, [addCommentAdoId]: Array.isArray(data) ? data : [] }));
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err?.message || 'Failed to add comment.');
    } finally {
      setSubmittingCommentAdoId(null);
    }
  };

  const openViewCommentsDialog = async (adoId, title) => {
    setViewCommentsAdoId(adoId);
    setViewCommentsTitle(title || adoId);
    setViewCommentsDialogOpen(true);
    setViewCommentsLoading(true);
    setViewCommentsList([]);
    try {
      const res = await getComments(adoId);
      const data = res?.data ?? res ?? [];
      setViewCommentsList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading comments:', err);
      setViewCommentsList([]);
    } finally {
      setViewCommentsLoading(false);
    }
  };

  const closeViewCommentsDialog = () => {
    setViewCommentsDialogOpen(false);
    setViewCommentsAdoId(null);
    setViewCommentsTitle('');
    setViewCommentsList([]);
  };

  // Toggle expand comment row in Weekly Report table; fetch comments when expanding if not cached
  const toggleCommentRowExpand = async (adoId) => {
    const nextExpanded = expandedCommentAdoId === adoId ? null : adoId;
    setExpandedCommentAdoId(nextExpanded);
    setCommentRowTabValue(0);
    if (nextExpanded && rowCommentsCache[nextExpanded] === undefined) {
      setRowCommentsLoadingAdoId(nextExpanded);
      try {
        const res = await getComments(nextExpanded);
        const data = res?.data ?? res ?? [];
        const list = Array.isArray(data) ? data : [];
        setRowCommentsCache((prev) => ({ ...prev, [nextExpanded]: list }));
      } catch (err) {
        console.error('Error loading comments for row:', err);
        setRowCommentsCache((prev) => ({ ...prev, [nextExpanded]: [] }));
      } finally {
        setRowCommentsLoadingAdoId(null);
      }
    }
  };

  const fetchReportList = async () => {
    setReportsLoading(true);
    try {
      const res = await listWSRReports();
      const data = res?.data ?? res ?? [];
      setReportList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading reports:', err);
      setReportList([]);
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === 1) fetchReportList();
  }, [selectedTab]);

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      setError(null);
      await generateWSRReport();
      await fetchReportList();
      if (selectedTab !== 1) setSelectedTab(1);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err?.message || 'Failed to generate report.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDeleteReport = async (report) => {
    const label = report.reportId || report.pdfFilename || report.filename || 'report';
    if (!window.confirm(`Delete report "${label}" (PDF and HTML)? This cannot be undone.`)) return;
    const filename = report.pdfFilename || report.htmlFilename || report.filename;
    if (!filename) return;
    try {
      setDeletingReportFilename(filename);
      setError(null);
      await deleteWSRReport(filename);
      setReportList((prev) => prev.filter((r) => (r.reportId || r.filename) !== (report.reportId || report.filename)));
    } catch (err) {
      console.error('Error deleting report:', err);
      setError(err?.message || 'Failed to delete report.');
    } finally {
      setDeletingReportFilename(null);
    }
  };

  const handleViewLog = async (report) => {
    const logFilename = report.logFilename;
    if (!logFilename) return;
    setLogDialogTitle(`Generation log — ${formatDateTime(report.generatedAt)}`);
    setLogDialogOpen(true);
    setLogDialogContent('');
    setLogDialogLoading(true);
    try {
      const text = await getWSRReportLog(logFilename);
      setLogDialogContent(typeof text === 'string' ? text : (text?.data ?? JSON.stringify(text)));
    } catch (err) {
      setLogDialogContent(`Error loading log: ${err?.message || 'Unknown error'}`);
    } finally {
      setLogDialogLoading(false);
    }
  };

  // Open bugs or test cases popup and fetch list
  const handleOpenListDialog = async (type, adoId, itemTitle) => {
    setListDialogOpen(true);
    setListDialogType(type);
    setListDialogAdoId(adoId);
    setListDialogTitle(itemTitle || adoId);
    setListDialogItems([]);
    setListDialogLoading(true);
    try {
      const response = type === 'bugs' ? await getWSRItemBugs(adoId) : await getWSRItemTestCases(adoId);
      const data = response.data ?? response;
      setListDialogItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(`Error loading ${type} for ${adoId}:`, err);
      setListDialogItems([]);
    } finally {
      setListDialogLoading(false);
    }
  };

  const handleCloseListDialog = () => {
    setListDialogOpen(false);
    setListDialogType(null);
    setListDialogAdoId(null);
    setListDialogTitle('');
    setListDialogItems([]);
  };

  // Handle refresh single item: get descendants, update work items/tests, build hierarchy (per-item only)
  const handleRefreshRow = async (adoId) => {
    if (!canAddItems) return;
    try {
      setRefreshingAdoId(adoId);
      setError(null);
      await refreshWSRItem(adoId);
      const detailsResponse = await getWSRItemDetails(adoId);
      const details = detailsResponse.data || detailsResponse;
      setWsrItemDetails((prev) => ({ ...prev, [adoId]: details }));
      await fetchWSRItems({ loadDetails: false });
    } catch (err) {
      console.error(`Error refreshing WSR item ${adoId}:`, err);
      setError(err.message || `Failed to refresh item ${adoId} from ADO.`);
    } finally {
      setRefreshingAdoId(null);
    }
  };


  // Get ADO URL
  const getAdoUrl = (adoId) => {
    return `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${adoId}`;
  };

  // Status color (Green, Amber, Red) - hex for consistent display
  const getStatusColor = (status) => {
    if (!status) return 'transparent';
    switch (status) {
      case 'Green': return '#4caf50';
      case 'Amber': return '#ff9800';
      case 'Red': return '#f44336';
      default: return 'transparent';
    }
  };

  // Colored status indicator (no text)
  const StatusIndicator = ({ status, size = 24 }) => {
    const color = getStatusColor(status);
    if (!color || color === 'transparent') return <Typography component="span" variant="body2" color="text.secondary">—</Typography>;
    return (
      <Tooltip title={status}>
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: size,
            height: size,
            borderRadius: 1,
            backgroundColor: color,
            border: '1px solid',
            borderColor: 'divider'
          }}
        />
      </Tooltip>
    );
  };

  // Format completion percentage
  const formatCompletion = (percentage) => {
    if (percentage === null || percentage === undefined) return 'N/A';
    return `${percentage}%`;
  };

  // Format story points
  const formatStoryPoints = (points) => {
    if (points === null || points === undefined) return 'N/A';
    return points.toString();
  };

  // Create team to stream mapping
  const teamToStreamMap = useMemo(() => {
    const map = new Map();
    teamsWithStreams.forEach(team => {
      // Map both id and displayName to stream for flexibility
      if (team.id) map.set(team.id, team.stream || 'UnMapped');
      if (team.displayName) map.set(team.displayName, team.stream || 'UnMapped');
    });
    return map;
  }, [teamsWithStreams]);

  // Get stream for a team
  const getStreamForTeam = useMemo(() => {
    return (team) => {
      if (!team || team === 'N/A' || team.trim() === '') {
        return 'UnMapped';
      }
      return teamToStreamMap.get(team) || 'UnMapped';
    };
  }, [teamToStreamMap]);

  // Get all unique streams
  const streams = useMemo(() => {
    const streamSet = new Set();
    teamsWithStreams.forEach(team => {
      const stream = team.stream || 'UnMapped';
      streamSet.add(stream);
    });
    // Always include UnMapped
    streamSet.add('UnMapped');
    return Array.from(streamSet).sort();
  }, [teamsWithStreams]);

  // Tab 0 = Weekly Report, 1 = Reports, 2+ = stream tabs
  const isWeeklyReportTab = selectedTab === 0;
  const isReportsTab = selectedTab === 1;
  const currentStream = isWeeklyReportTab || isReportsTab ? null : (streams[selectedTab - 2] || streams[0] || 'UnMapped');

  // Stream-tab rows for current stream before type/target/completed filters (for option lists and counts)
  const streamTabItemsUnfiltered = useMemo(() => {
    if (isWeeklyReportTab || isReportsTab || currentStream == null) return [];
    return itemsForStreamTabs.filter((item) => {
      const details = wsrItemDetails[item.adoId];
      const team = details?.team || '';
      return getStreamForTeam(team) === currentStream;
    });
  }, [itemsForStreamTabs, wsrItemDetails, currentStream, getStreamForTeam, isWeeklyReportTab, isReportsTab]);

  // Items that are in the Weekly Report tab (flat list, no hierarchy)
  const weeklyReportItems = useMemo(() => {
    return wsrItems.filter(item => item.inWeeklyReport === true);
  }, [wsrItems]);

  // Epic / Feature / PBI options present in the current tab’s items
  const workItemTypesForFilterOptions = useMemo(() => {
    const sourceItems = isWeeklyReportTab ? weeklyReportItems : streamTabItemsUnfiltered;
    const found = new Set();
    sourceItems.forEach((item) => {
      const wit = wsrItemDetails[item.adoId]?.workItemType;
      if (wit && WSR_TYPE_FILTER_TYPES.includes(wit)) found.add(wit);
    });
    return [...found].sort((a, b) => (WSR_TYPE_FILTER_ORDER[a] ?? 99) - (WSR_TYPE_FILTER_ORDER[b] ?? 99));
  }, [isWeeklyReportTab, weeklyReportItems, streamTabItemsUnfiltered, wsrItemDetails]);

  const typeTargetCompletedFilterOpts = useMemo(
    () => ({ selectedWorkItemTypes, targetDateBefore, excludeCompleted }),
    [selectedWorkItemTypes, targetDateBefore, excludeCompleted]
  );

  // Teams that appear in Weekly Report items (for Filter by Team on Weekly Report tab)
  const availableTeamsForWeeklyReport = useMemo(() => {
    const teamsSet = new Set();
    weeklyReportItems.forEach(item => {
      const details = wsrItemDetails[item.adoId];
      if (details?.team && details.team.trim() !== '' && details.team !== 'N/A') {
        teamsSet.add(details.team);
      }
    });
    return Array.from(teamsSet).sort();
  }, [weeklyReportItems, wsrItemDetails]);

  // Filtered Weekly Report items by team, status (weekly tab only), type, target date, exclude completed
  const filteredWeeklyReportItems = useMemo(() => {
    let items = weeklyReportItems;
    if (selectedTeams.length > 0) {
      items = items.filter(item => {
        const details = wsrItemDetails[item.adoId];
        const team = details?.team || '';
        return selectedTeams.includes(team);
      });
    }
    if (selectedStatuses.length > 0) {
      items = items.filter(item => {
        const status = getStatusForAdo(item.adoId);
        return selectedStatuses.includes(status);
      });
    }
    items = items.filter((item) =>
      wsrItemPassesTypeTargetCompletedFilters(wsrItemDetails[item.adoId], typeTargetCompletedFilterOpts)
    );
    return items;
  }, [weeklyReportItems, wsrItemDetails, selectedTeams, selectedStatuses, wsrItems, typeTargetCompletedFilterOpts]);

  // Status order for sort: Green first, then Amber, then Red, then empty
  const statusSortOrder = useMemo(() => ({ Green: 0, Amber: 1, Red: 2, '': 3 }), []);

  // Sorted Weekly Report items for table display (primary sort, then secondary by team)
  const sortedWeeklyReportItems = useMemo(() => {
    const items = [...filteredWeeklyReportItems];
    const getDetail = (item, field) => {
      const d = wsrItemDetails[item.adoId];
      switch (field) {
        case 'adoId': return String(item.adoId);
        case 'title': return (d?.title || '').toLowerCase();
        case 'workItemType': return (d?.workItemType || '').toLowerCase();
        case 'status': return (getStatusForAdo(item.adoId) || '').toLowerCase();
        case 'team': return (d?.team || '').toLowerCase();
        case 'storyPoints': return d?.totalStoryPoints ?? -1;
        case 'completionPercentage': return d?.completionPercentage ?? -1;
        case 'targetDate': return d?.targetDate ? new Date(d.targetDate).getTime() : 0;
        case 'fixVersion': return (d?.fixVersion || '').toLowerCase();
        case 'bugsCount': return d?.bugsCount ?? -1;
        case 'testCasesCount': return d?.testCasesCount ?? -1;
        default: return '';
      }
    };
    const getStatusRank = (item) => statusSortOrder[getStatusForAdo(item.adoId)] ?? 4;
    const getTeamStr = (item) => (wsrItemDetails[item.adoId]?.team || '').toLowerCase();
    items.sort((a, b) => {
      let cmp = 0;
      if (wsrSortBy === 'status') {
        const aRank = getStatusRank(a);
        const bRank = getStatusRank(b);
        cmp = aRank - bRank;
        if (cmp === 0) cmp = getTeamStr(a).localeCompare(getTeamStr(b));
      } else if (wsrSortBy === 'team') {
        cmp = getTeamStr(a).localeCompare(getTeamStr(b));
        if (cmp === 0) cmp = getStatusRank(a) - getStatusRank(b);
      } else {
        const aVal = getDetail(a, wsrSortBy);
        const bVal = getDetail(b, wsrSortBy);
        if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
        else if (typeof aVal === 'string' && typeof bVal === 'string') cmp = aVal.localeCompare(bVal);
        else cmp = String(aVal).localeCompare(String(bVal));
        if (cmp === 0) cmp = getStatusRank(a) - getStatusRank(b);
        if (cmp === 0) cmp = getTeamStr(a).localeCompare(getTeamStr(b));
      }
      return wsrSortOrder === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [filteredWeeklyReportItems, wsrItemDetails, wsrSortBy, wsrSortOrder, wsrItems, statusSortOrder]);

  const handleWsrRequestSort = (property) => {
    const isAsc = wsrSortBy === property && wsrSortOrder === 'asc';
    setWsrSortOrder(isAsc ? 'desc' : 'asc');
    setWsrSortBy(property);
  };

  // Set of ADO IDs in Weekly Report for reliable row highlighting across all stream tabs (type-safe)
  const weeklyReportAdoIds = useMemo(() => {
    return new Set(
      wsrItems
        .filter(item => Boolean(item.inWeeklyReport))
        .map(item => String(item.adoId))
    );
  }, [wsrItems]);

  // Get unique teams from items shown in stream tabs (current quarter or saved WSR)
  const allAvailableTeams = useMemo(() => {
    const teamsSet = new Set();
    itemsForStreamTabs.forEach(item => {
      const details = wsrItemDetails[item.adoId];
      if (details?.team && details.team.trim() !== '' && details.team !== 'N/A') {
        teamsSet.add(details.team);
      }
    });
    return Array.from(teamsSet).sort();
  }, [itemsForStreamTabs, wsrItemDetails]);

  // Get teams for the current stream only (used when on stream tabs)
  const availableTeams = useMemo(() => {
    if (!currentStream || currentStream === 'UnMapped') {
      // For UnMapped, show all teams that don't have a stream assigned
      return allAvailableTeams.filter(team => {
        const stream = getStreamForTeam(team);
        return stream === 'UnMapped';
      });
    }

    // For other streams, show only teams that belong to this stream
    return allAvailableTeams.filter(team => {
      const stream = getStreamForTeam(team);
      return stream === currentStream;
    });
  }, [allAvailableTeams, currentStream, getStreamForTeam]);

  // Which teams to show in the Filter by Team dropdown (Weekly Report and stream tabs only)
  const teamsForFilter = isWeeklyReportTab ? availableTeamsForWeeklyReport : (isReportsTab ? [] : availableTeams);

  // Filter items (for stream tabs): team + type/target/completed — not Green/Amber/Red (weekly tab only)
  const filteredWSRItems = useMemo(() => {
    let items = itemsForStreamTabs;
    if (selectedTeams.length > 0) {
      items = items.filter(item => {
        const details = wsrItemDetails[item.adoId];
        const team = details?.team || '';
        return selectedTeams.includes(team);
      });
    }
    items = items.filter((item) =>
      wsrItemPassesTypeTargetCompletedFilters(wsrItemDetails[item.adoId], typeTargetCompletedFilterOpts)
    );
    return items;
  }, [itemsForStreamTabs, wsrItemDetails, selectedTeams, typeTargetCompletedFilterOpts]);

  // Group items by stream (for stream tabs)
  const wsrItemsByStream = useMemo(() => {
    const grouped = {};
    streams.forEach(stream => {
      grouped[stream] = [];
    });

    filteredWSRItems.forEach(item => {
      const details = wsrItemDetails[item.adoId];
      const team = details?.team || '';
      const stream = getStreamForTeam(team);
      if (grouped[stream]) {
        grouped[stream].push(item);
      } else {
        // If stream not found, add to UnMapped
        if (grouped['UnMapped']) {
          grouped['UnMapped'].push(item);
        }
      }
    });

    return grouped;
  }, [filteredWSRItems, wsrItemDetails, streams, getStreamForTeam]);

  const currentStreamItems = wsrItemsByStream[currentStream] || [];

  // Organize items into Epic -> Features tree structure
  const treeStructure = useMemo(() => {
    const epics = [];
    const features = [];
    const epicMap = new Map();

    // First pass: identify Epics and Features
    currentStreamItems.forEach(item => {
      const details = wsrItemDetails[item.adoId];
      if (!details) return;

      if (details.workItemType === 'Epic') {
        epics.push({ ...item, details, features: [] });
        epicMap.set(item.adoId, epics[epics.length - 1]);
      } else if (details.workItemType === 'Feature') {
        features.push({ ...item, details });
      }
    });

    // Second pass: link Features to their parent Epics
    features.forEach(feature => {
      const parentId = feature.details.parent || null;
      
      if (parentId && epicMap.has(parentId)) {
        epicMap.get(parentId).features.push(feature);
      } else {
        // Orphaned feature - add as standalone (will be shown as a feature without parent)
        epics.push({ ...feature, details: feature.details, features: [], isOrphaned: true });
      }
    });

    return epics;
  }, [currentStreamItems, wsrItemDetails]);

  // Toggle epic expansion
  const toggleEpic = (epicId) => {
    setExpandedEpics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(epicId)) {
        newSet.delete(epicId);
      } else {
        newSet.add(epicId);
      }
      return newSet;
    });
  };

  // Handle team filter change
  const handleTeamFilterChange = (event) => {
    setSelectedTeams(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setSelectedStatuses(event.target.value);
  };

  // Clear filters when tab changes
  useEffect(() => {
    setSelectedTeams([]);
    setSelectedStatuses([]);
    setSelectedWorkItemTypes([]);
    setTargetDateBefore('');
    setExcludeCompleted(false);
  }, [selectedTab]);

  if (loading && itemsForStreamTabs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5">Weekly Status Report</Typography>
          {!WSR_BULK_DETAILS_FETCH_ENABLED && (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => fetchWSRItems({ loadDetails: true })}
                disabled={loading}
              >
                Load all work item details
              </Button>
              <Typography variant="caption" color="text.secondary">
                Bulk details are off (REACT_APP_WSR_AUTO_FETCH_ITEM_DETAILS=false). Restart dev server after changing .env.
              </Typography>
            </Box>
          )}
        </Box>
        <Box display="flex" gap={2}>
          {canAddItems && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add ADO Item
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!WSR_BULK_DETAILS_FETCH_ENABLED && (
        <Alert severity="info" sx={{ mb: 2 }}>
          WSR bulk fetch for work item details is disabled. Tables show limited data until you use &quot;Load all work item
          details&quot; or Refresh on a row.
        </Alert>
      )}

      {/* Weekly Report tab first, then stream tabs */}
      {streams.length >= 0 && (
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">Weekly Report</Typography>
                  <Chip
                    label={weeklyReportItems.length}
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">Reports</Typography>
                  <Chip
                    label={reportList.length}
                    size="small"
                    color="default"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              }
            />
            {streams.map((stream) => {
              const streamItems = wsrItemsByStream[stream] || [];
              return (
                <Tab
                  key={stream}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{stream}</Typography>
                      <Chip
                        label={streamItems.length}
                        size="small"
                        color="primary"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Paper>
      )}

      {/* Team, work item type, target date, exclude completed; Green/Amber/Red status only on Weekly Report */}
      {!isReportsTab && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {teamsForFilter.length > 0 && (
              <FormControl variant="outlined" size="small" sx={{ minWidth: 280 }}>
                <InputLabel id="team-filter-label">Filter by Team</InputLabel>
                <Select
                  labelId="team-filter-label"
                  id="team-filter"
                  multiple
                  value={selectedTeams}
                  onChange={handleTeamFilterChange}
                  input={<OutlinedInput label="Filter by Team" />}
                  renderValue={(selected) => (
                    selected.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">All Teams</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        width: 250,
                      },
                    },
                  }}
                >
                  {selectedTeams.length > 0 && (
                    <MenuItem onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTeams([]);
                    }}>
                      <em>Clear Selection</em>
                    </MenuItem>
                  )}
                  {teamsForFilter.map((team) => (
                    <MenuItem key={team} value={team}>
                      <Checkbox checked={selectedTeams.indexOf(team) > -1} />
                      <ListItemText primary={team} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {workItemTypesForFilterOptions.length > 0 && (
              <FormControl variant="outlined" size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="wit-filter-label">Work item type</InputLabel>
                <Select
                  labelId="wit-filter-label"
                  id="wit-filter"
                  multiple
                  value={selectedWorkItemTypes}
                  onChange={(e) => setSelectedWorkItemTypes(e.target.value)}
                  input={<OutlinedInput label="Work item type" />}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">All types</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={WSR_WORK_ITEM_TYPE_LABEL[value] || value} size="small" />
                        ))}
                      </Box>
                    )
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        width: 260
                      }
                    }
                  }}
                >
                  {selectedWorkItemTypes.length > 0 && (
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedWorkItemTypes([]);
                      }}
                    >
                      <em>Clear selection</em>
                    </MenuItem>
                  )}
                  {workItemTypesForFilterOptions.map((wit) => (
                    <MenuItem key={wit} value={wit}>
                      <Checkbox checked={selectedWorkItemTypes.indexOf(wit) > -1} />
                      <ListItemText primary={WSR_WORK_ITEM_TYPE_LABEL[wit] || wit} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Target date before"
              type="date"
              size="small"
              value={targetDateBefore}
              onChange={(e) => setTargetDateBefore(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={excludeCompleted}
                  onChange={(e) => setExcludeCompleted(e.target.checked)}
                  size="small"
                />
              }
              label="Exclude completed"
            />
            {isWeeklyReportTab && (
              <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusFilterChange}
                  input={<OutlinedInput label="Filter by Status" />}
                  renderValue={(selected) => (
                    selected.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">All Statuses</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(value),
                              color: value === 'Amber' ? '#000' : '#fff',
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          />
                        ))}
                      </Box>
                    )
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        width: 220
                      }
                    }
                  }}
                >
                  {selectedStatuses.length > 0 && (
                    <MenuItem onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStatuses([]);
                    }}>
                      <em>Clear Selection</em>
                    </MenuItem>
                  )}
                  {WSR_STATUS_FILTER_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: 0.5,
                            backgroundColor: getStatusColor(status),
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        />
                        <ListItemText primary={status} />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {(selectedTeams.length > 0 ||
              (isWeeklyReportTab && selectedStatuses.length > 0) ||
              selectedWorkItemTypes.length > 0 ||
              Boolean(targetDateBefore) ||
              excludeCompleted) && (
              <Typography variant="body2" color="text.secondary">
                {isWeeklyReportTab
                  ? `Showing ${filteredWeeklyReportItems.length} of ${weeklyReportItems.length} items`
                  : `Showing ${filteredWSRItems.length} of ${streamTabItemsUnfiltered.length} items in this stream`}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {isReportsTab ? (
        /* Reports tab: list of generated PDF reports with timestamp and link */
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Generated Reports</Typography>
          {reportsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : reportList.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
              No reports yet. Go to the Weekly Report tab and click &quot;Generate Report&quot; to create a PDF.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Generated (Date / Time)</TableCell>
                    <TableCell>PDF</TableCell>
                    <TableCell>HTML</TableCell>
                    <TableCell>Log</TableCell>
                    {canAddItems && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportList.map((report) => {
                    const reportKey = report.reportId || report.filename;
                    const pdfFilename = report.pdfFilename || (report.filename && report.filename.endsWith('.pdf') ? report.filename : null);
                    const htmlFilename = report.htmlFilename || null;
                    return (
                      <TableRow key={reportKey} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(report.generatedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {pdfFilename ? (
                            <MuiLink
                              component="a"
                              href={`/api/wsr/reports/${encodeURIComponent(pdfFilename)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                              PDF
                              <OpenInNewIcon fontSize="small" />
                            </MuiLink>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {htmlFilename ? (
                            <MuiLink
                              component="a"
                              href={`/api/wsr/reports/${encodeURIComponent(htmlFilename)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                              HTML
                              <OpenInNewIcon fontSize="small" />
                            </MuiLink>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {report.logFilename ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewLog(report)}
                              sx={{ textTransform: 'none' }}
                            >
                              View log
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        {canAddItems && (
                          <TableCell align="right">
                            <Tooltip title="Delete report (PDF and HTML)">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteReport(report)}
                                disabled={deletingReportFilename === (pdfFilename || htmlFilename)}
                              >
                                {deletingReportFilename === (pdfFilename || htmlFilename) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Dialog
            open={logDialogOpen}
            onClose={() => setLogDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { minHeight: '60vh' } }}
          >
            <DialogTitle>{logDialogTitle}</DialogTitle>
            <DialogContent dividers>
              {logDialogLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem'
                  }}
                >
                  {logDialogContent || 'No log content.'}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setLogDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Paper>
      ) : isWeeklyReportTab ? (
        /* Weekly Report tab: flat list, details, Delete (remove from report), Comments. No Refresh. */
        <Paper sx={{ p: 2 }}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
            {canGenerateReport && (
              <Button
                variant="contained"
                color="primary"
                startIcon={generatingReport ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport || weeklyReportItems.length === 0}
              >
                {generatingReport ? 'Generating Report…' : 'Generate Report'}
              </Button>
            )}
          </Box>
          {weeklyReportItems.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
              No items in Weekly Report. Use &quot;Move To Weekly Report&quot; on any ADO item in the stream tabs to add it here.
            </Typography>
          ) : filteredWeeklyReportItems.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
              No items match the selected team filter. Clear the filter to see all {weeklyReportItems.length} items.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={wsrSortBy === 'title' ? wsrSortOrder : false}>
                      <TableSortLabel
                        active={wsrSortBy === 'title'}
                        direction={wsrSortBy === 'title' ? wsrSortOrder : 'asc'}
                        onClick={() => handleWsrRequestSort('title')}
                      >
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={wsrSortBy === 'status' ? wsrSortOrder : false}>
                      <TableSortLabel
                        active={wsrSortBy === 'status'}
                        direction={wsrSortBy === 'status' ? wsrSortOrder : 'asc'}
                        onClick={() => handleWsrRequestSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={wsrSortBy === 'completionPercentage' ? wsrSortOrder : false}>
                      <TableSortLabel
                        active={wsrSortBy === 'completionPercentage'}
                        direction={wsrSortBy === 'completionPercentage' ? wsrSortOrder : 'asc'}
                        onClick={() => handleWsrRequestSort('completionPercentage')}
                      >
                        Completion %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={wsrSortBy === 'targetDate' ? wsrSortOrder : false}>
                      <TableSortLabel
                        active={wsrSortBy === 'targetDate'}
                        direction={wsrSortBy === 'targetDate' ? wsrSortOrder : 'asc'}
                        onClick={() => handleWsrRequestSort('targetDate')}
                      >
                        Target Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={wsrSortBy === 'fixVersion' ? wsrSortOrder : false}>
                      <TableSortLabel
                        active={wsrSortBy === 'fixVersion'}
                        direction={wsrSortBy === 'fixVersion' ? wsrSortOrder : 'asc'}
                        onClick={() => handleWsrRequestSort('fixVersion')}
                      >
                        Fix version
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={wsrSortBy === 'bugsCount' ? wsrSortOrder : false}>
                      <TableSortLabel
                        active={wsrSortBy === 'bugsCount'}
                        direction={wsrSortBy === 'bugsCount' ? wsrSortOrder : 'asc'}
                        onClick={() => handleWsrRequestSort('bugsCount')}
                      >
                        Bugs
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={wsrSortBy === 'testCasesCount' ? wsrSortOrder : false}>
                      <TableSortLabel
                        active={wsrSortBy === 'testCasesCount'}
                        direction={wsrSortBy === 'testCasesCount' ? wsrSortOrder : 'asc'}
                        onClick={() => handleWsrRequestSort('testCasesCount')}
                      >
                        Test Cases
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Comments</TableCell>
                    <TableCell padding="checkbox">PRD</TableCell>
                    <TableCell padding="checkbox">Design</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedWeeklyReportItems.map((item) => {
                    const details = wsrItemDetails[item.adoId];
                    const isLoadingDetails = WSR_BULK_DETAILS_FETCH_ENABLED && !details && !error;
                    return (
                      <React.Fragment key={item.adoId}>
                        <TableRow hover>
                          <TableCell>
                            {isLoadingDetails ? (
                              <CircularProgress size={16} />
                            ) : (
                              <>
                                <Typography variant="body2" component="div">
                                  {details?.title || (WSR_BULK_DETAILS_FETCH_ENABLED ? 'Loading...' : '—')}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                                  <Chip
                                    component="a"
                                    href={getAdoUrl(item.adoId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    label={item.adoId}
                                    size="small"
                                    variant="outlined"
                                    clickable
                                    icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                                    sx={{ textDecoration: 'none' }}
                                  />
                                  {details?.workItemType && (
                                    <Chip label={details.workItemType} size="small" variant="outlined" />
                                  )}
                                  <Chip
                                    label={`Story Points: ${formatStoryPoints(details?.totalStoryPoints)}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  {details?.team && details.team !== 'N/A' && (
                                    <Chip label={details.team} size="small" variant="outlined" />
                                  )}
                                </Box>
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            {canAddItems ? (
                              <Select
                                size="small"
                                value={getStatusForAdo(item.adoId) || ''}
                                displayEmpty
                                onChange={(e) => handleStatusChange(item.adoId, e.target.value || null)}
                                disabled={statusUpdatingAdoId === item.adoId}
                                renderValue={(value) => (value ? <StatusIndicator status={value} size={22} /> : '—')}
                                sx={{ minWidth: 44, height: 36 }}
                              >
                                <MenuItem value="">
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                </MenuItem>
                                <MenuItem value="Green">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: '#4caf50', border: '1px solid', borderColor: 'divider' }} />
                                    <Typography variant="body2">Green</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="Amber">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: '#ff9800', border: '1px solid', borderColor: 'divider' }} />
                                    <Typography variant="body2">Amber</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="Red">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: '#f44336', border: '1px solid', borderColor: 'divider' }} />
                                    <Typography variant="body2">Red</Typography>
                                  </Box>
                                </MenuItem>
                              </Select>
                            ) : (
                              <StatusIndicator status={getStatusForAdo(item.adoId)} size={22} />
                            )}
                          </TableCell>
                          <TableCell>
                            {details?.completionPercentage !== undefined ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={details.completionPercentage}
                                  sx={{ flexGrow: 1, height: 6, borderRadius: 2 }}
                                />
                                <Typography variant="body2" sx={{ minWidth: 36 }}>
                                  {formatCompletion(details.completionPercentage)}
                                </Typography>
                              </Box>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            {details?.targetDate ? formatDate(details.targetDate) : 'N/A'}
                          </TableCell>
                          <TableCell>{details?.fixVersion || 'N/A'}</TableCell>
                          <TableCell>
                            {details?.bugsCount !== undefined ? (
                              details.bugsCount > 0 ? (
                                <MuiLink
                                  component="button"
                                  variant="body2"
                                  onClick={() => handleOpenListDialog('bugs', item.adoId, details?.title)}
                                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                  {details.bugsCount}
                                </MuiLink>
                              ) : (
                                <Typography variant="body2">{details.bugsCount}</Typography>
                              )
                            ) : (
                              isLoadingDetails ? <CircularProgress size={16} /> : 'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            {details?.testCasesCount !== undefined ? (
                              details.testCasesCount > 0 ? (
                                <MuiLink
                                  component="button"
                                  variant="body2"
                                  onClick={() => handleOpenListDialog('test-cases', item.adoId, details?.title)}
                                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                  {details.testCasesCount}
                                </MuiLink>
                              ) : (
                                <Typography variant="body2">{details.testCasesCount}</Typography>
                              )
                            ) : (
                              isLoadingDetails ? <CircularProgress size={16} /> : 'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => toggleCommentRowExpand(item.adoId)}
                                aria-label={expandedCommentAdoId === item.adoId ? 'Collapse comments' : 'Expand comments'}
                              >
                                {expandedCommentAdoId === item.adoId ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                              <MuiLink
                                component="button"
                                variant="body2"
                                onClick={() => openViewCommentsDialog(item.adoId, details?.title)}
                                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                              >
                                Comments
                              </MuiLink>
                            </Box>
                          </TableCell>
                          <TableCell padding="checkbox">
                            {canAddItems ? (
                              <Checkbox
                                checked={Boolean(item.prd)}
                                disabled={prdDesignUpdatingAdoId === item.adoId}
                                onChange={(e) => handlePrdDesignChange(item.adoId, 'prd', e.target.checked)}
                                size="small"
                                title="PRD relevant"
                              />
                            ) : (
                              item.prd ? '✓' : '—'
                            )}
                          </TableCell>
                          <TableCell padding="checkbox">
                            {canAddItems ? (
                              <Checkbox
                                checked={Boolean(item.design)}
                                disabled={prdDesignUpdatingAdoId === item.adoId}
                                onChange={(e) => handlePrdDesignChange(item.adoId, 'design', e.target.checked)}
                                size="small"
                                title="Design relevant"
                              />
                            ) : (
                              item.design ? '✓' : '—'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" gap={0.5} justifyContent="flex-end">
                              <Tooltip title="Add comment">
                                <IconButton
                                  size="small"
                                  onClick={() => openAddCommentDialog(item.adoId, details?.title)}
                                >
                                  <CommentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {canAddItems && (
                                <Tooltip title="Remove from Weekly Report">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveFromWeeklyReport(item.adoId)}
                                    disabled={removingFromReportAdoId === item.adoId}
                                  >
                                    {removingFromReportAdoId === item.adoId ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        {/* Expandable row: last comment in tabbed view */}
                        <TableRow>
                          <TableCell colSpan={11} sx={{ py: 0, px: 0, borderBottom: expandedCommentAdoId === item.adoId ? undefined : 'none', verticalAlign: 'top' }}>
                            <Collapse in={expandedCommentAdoId === item.adoId} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                                {rowCommentsLoadingAdoId === item.adoId ? (
                                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                                    <CircularProgress size={28} />
                                  </Box>
                                ) : (
                                  <>
                                    <Tabs value={commentRowTabValue} onChange={(_, v) => setCommentRowTabValue(v)} sx={{ minHeight: 36, mb: 1 }}>
                                      <Tab label="Last comment" id={`comment-tab-${item.adoId}-0`} aria-controls={`comment-panel-${item.adoId}-0`} />
                                      <Tab label="All comments" id={`comment-tab-${item.adoId}-1`} aria-controls={`comment-panel-${item.adoId}-1`} />
                                    </Tabs>
                                    {commentRowTabValue === 0 && (
                                      <Box role="tabpanel" id={`comment-panel-${item.adoId}-0`} aria-labelledby={`comment-tab-${item.adoId}-0`}>
                                        {(() => {
                                          const list = rowCommentsCache[item.adoId] || [];
                                          const sorted = [...list].sort((a, b) => (new Date(b.timestamp || 0)).getTime() - (new Date(a.timestamp || 0)).getTime());
                                          const lastComment = sorted[0];
                                          if (!lastComment) {
                                            return (
                                              <Typography variant="body2" color="text.secondary">
                                                No comments yet. Use &quot;Comments&quot; to view or add.
                                              </Typography>
                                            );
                                          }
                                          return (
                                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                              {renderCommentBody(lastComment)}
                                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                {lastComment.user || 'Unknown'} — {formatDateTime(lastComment.timestamp)}
                                              </Typography>
                                            </Paper>
                                          );
                                        })()}
                                      </Box>
                                    )}
                                    {commentRowTabValue === 1 && (
                                      <Box role="tabpanel" id={`comment-panel-${item.adoId}-1`} aria-labelledby={`comment-tab-${item.adoId}-1`}>
                                        {(rowCommentsCache[item.adoId] || []).length === 0 ? (
                                          <Typography variant="body2" color="text.secondary">
                                            No comments yet.
                                          </Typography>
                                        ) : (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {([...(rowCommentsCache[item.adoId] || [])]
                                              .sort((a, b) => (new Date(b.timestamp || 0)).getTime() - (new Date(a.timestamp || 0)).getTime())
                                              .map((c) => (
                                              <Paper key={c._id || c.timestamp} variant="outlined" sx={{ p: 1.5 }}>
                                                {renderCommentBody(c)}
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                  {c.user || 'Unknown'} — {formatDateTime(c.timestamp)}
                                                </Typography>
                                              </Paper>
                                            )))}
                                          </Box>
                                        )}
                                        <Button
                                          size="small"
                                          startIcon={<CommentIcon />}
                                          onClick={() => openViewCommentsDialog(item.adoId, details?.title)}
                                          sx={{ mt: 1 }}
                                        >
                                          View all / Add comment
                                        </Button>
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            </Collapse>
                            </TableCell>
                        {/* end expandable comment row */}
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      ) : itemsForStreamTabs.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No Epics or Features found for the current quarter. {canAddItems && 'Click "Add ADO Item" to add items manually.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 50 }}></TableCell>
                <TableCell>ADO Id</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>WorkItemType</TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Total Story Points</TableCell>
                <TableCell>Completion %</TableCell>
                <TableCell>Target Date</TableCell>
                <TableCell>Fix version</TableCell>
                <TableCell>Bugs</TableCell>
                <TableCell>Test Cases</TableCell>
                <TableCell>Comments</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {treeStructure.map((epic) => {
                const details = epic.details;
                const isLoadingDetails = WSR_BULK_DETAILS_FETCH_ENABLED && !details && !error;
                const isEpic = details?.workItemType === 'Epic' && !epic.isOrphaned;
                const hasFeatures = epic.features && epic.features.length > 0;
                const isExpanded = expandedEpics.has(epic.adoId);
                // Use inWeeklyReport from epic (spread from item) so it works on every stream tab
                const epicInWeeklyReport =
                  Boolean(epic.inWeeklyReport) || weeklyReportAdoIds.has(String(epic.adoId));

                return (
                  <React.Fragment key={epic.adoId}>
                    <TableRow hover sx={{ backgroundColor: isEpic ? 'action.hover' : 'transparent' }}>
                      <TableCell>
                        {isEpic && hasFeatures && (
                          <IconButton
                            size="small"
                            onClick={() => toggleEpic(epic.adoId)}
                            sx={{ padding: 0.5 }}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <MuiLink
                          href={getAdoUrl(epic.adoId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {epic.adoId}
                          <OpenInNewIcon fontSize="small" />
                        </MuiLink>
                      </TableCell>
                      <TableCell>
                        {isLoadingDetails ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              fontWeight: isEpic ? 600 : 'normal',
                              ...(epicInWeeklyReport && {
                                color: '#1976d2',
                                display: 'block'
                              })
                            }}
                          >
                            {details?.title || (WSR_BULK_DETAILS_FETCH_ENABLED ? 'Loading...' : '—')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {details?.workItemType ? (
                          <Chip
                            label={details.workItemType}
                            size="small"
                            color={
                              details.workItemType === 'Epic' ? 'primary' :
                              details.workItemType === 'Feature' ? 'info' :
                              details.workItemType === 'Product Backlog Item' ? 'success' :
                              details.workItemType === 'Bug' ? 'error' :
                              'default'
                            }
                            variant="outlined"
                          />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{details?.team || 'N/A'}</TableCell>
                      <TableCell>{formatStoryPoints(details?.totalStoryPoints)}</TableCell>
                      <TableCell>
                        {details?.completionPercentage !== undefined ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                            <LinearProgress
                              variant="determinate"
                              value={details.completionPercentage}
                              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" sx={{ minWidth: 40 }}>
                              {formatCompletion(details.completionPercentage)}
                            </Typography>
                          </Box>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {details?.targetDate ? formatDate(details.targetDate) : 'N/A'}
                      </TableCell>
                      <TableCell>{details?.fixVersion || 'N/A'}</TableCell>
                      <TableCell>
                        {details?.bugsCount !== undefined ? (
                          details.bugsCount > 0 ? (
                            <MuiLink
                              component="button"
                              variant="body2"
                              onClick={() => handleOpenListDialog('bugs', epic.adoId, details?.title)}
                              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              {details.bugsCount}
                            </MuiLink>
                          ) : (
                            <Typography variant="body2">{details.bugsCount}</Typography>
                          )
                        ) : (
                          isLoadingDetails ? <CircularProgress size={16} /> : 'N/A'
                        )}
                      </TableCell>
                        <TableCell>
                          {details?.testCasesCount !== undefined ? (
                            details.testCasesCount > 0 ? (
                              <MuiLink
                                component="button"
                                variant="body2"
                                onClick={() => handleOpenListDialog('test-cases', epic.adoId, details?.title)}
                                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                              >
                                {details.testCasesCount}
                              </MuiLink>
                            ) : (
                              <Typography variant="body2">{details.testCasesCount}</Typography>
                            )
                          ) : (
                            isLoadingDetails ? <CircularProgress size={16} /> : 'N/A'
                          )}
                        </TableCell>
                      <TableCell>
                        <MuiLink
                          component="button"
                          variant="body2"
                          onClick={() => openViewCommentsDialog(epic.adoId, details?.title)}
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Comments
                        </MuiLink>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          {canAddItems && (
                            <Tooltip title="Refresh from ADO">
                              <IconButton
                                size="small"
                                onClick={() => handleRefreshRow(epic.adoId)}
                                disabled={refreshingAdoId === epic.adoId}
                              >
                                {refreshingAdoId === epic.adoId ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {canAddItems && (
                            <Tooltip title="Add to Weekly Report">
                              <IconButton
                                size="small"
                                onClick={() => handleMoveToWeeklyReport(epic.adoId)}
                                disabled={epicInWeeklyReport || movingToReportAdoId === epic.adoId}
                              >
                                {movingToReportAdoId === epic.adoId ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <AddIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Add comment">
                            <IconButton
                              size="small"
                              onClick={() => openAddCommentDialog(epic.adoId, details?.title)}
                            >
                              <CommentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Render Features under Epic */}
                    {isEpic && hasFeatures && (
                      <TableRow>
                        <TableCell colSpan={13} sx={{ padding: 0, border: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ paddingLeft: 4, backgroundColor: 'grey.50' }}>
                              <Table size="small">
                                <TableBody>
                                  {epic.features.map((feature) => {
                                    const featureDetails = feature.details;
                                    const isLoadingFeature = WSR_BULK_DETAILS_FETCH_ENABLED && !featureDetails && !error;
                                    // Use inWeeklyReport from feature (spread from item) so it works on every stream tab
                                    const featureInWeeklyReport =
                                      Boolean(feature.inWeeklyReport) ||
                                      weeklyReportAdoIds.has(String(feature.adoId));

                                    return (
                                      <TableRow key={feature.adoId} hover>
                                        <TableCell sx={{ width: 50 }}></TableCell>
                                        <TableCell>
                                          <MuiLink
                                            href={getAdoUrl(feature.adoId)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                          >
                                            {feature.adoId}
                                            <OpenInNewIcon fontSize="small" />
                                          </MuiLink>
                                        </TableCell>
                                        <TableCell>
                                          {isLoadingFeature ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              component="span"
                                              sx={{
                                                ...(featureInWeeklyReport && {
                                                  color: '#1976d2',
                                                  display: 'block'
                                                })
                                              }}
                                            >
                                              {featureDetails?.title || (WSR_BULK_DETAILS_FETCH_ENABLED ? 'Loading...' : '—')}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {featureDetails?.workItemType ? (
                                            <Chip
                                              label={featureDetails.workItemType}
                                              size="small"
                                              color="info"
                                              variant="outlined"
                                            />
                                          ) : (
                                            'N/A'
                                          )}
                                        </TableCell>
                                        <TableCell>{featureDetails?.team || 'N/A'}</TableCell>
                                        <TableCell>{formatStoryPoints(featureDetails?.totalStoryPoints)}</TableCell>
                                        <TableCell>
                                          {featureDetails?.completionPercentage !== undefined ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                                              <LinearProgress
                                                variant="determinate"
                                                value={featureDetails.completionPercentage}
                                                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                              />
                                              <Typography variant="body2" sx={{ minWidth: 40 }}>
                                                {formatCompletion(featureDetails.completionPercentage)}
                                              </Typography>
                                            </Box>
                                          ) : (
                                            'N/A'
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {featureDetails?.targetDate ? formatDate(featureDetails.targetDate) : 'N/A'}
                                        </TableCell>
                                        <TableCell>{featureDetails?.fixVersion || 'N/A'}</TableCell>
                                        <TableCell>
                                          {featureDetails?.bugsCount !== undefined ? (
                                            featureDetails.bugsCount > 0 ? (
                                              <MuiLink
                                                component="button"
                                                variant="body2"
                                                onClick={() => handleOpenListDialog('bugs', feature.adoId, featureDetails?.title)}
                                                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                              >
                                                {featureDetails.bugsCount}
                                              </MuiLink>
                                            ) : (
                                              <Typography variant="body2">{featureDetails.bugsCount}</Typography>
                                            )
                                          ) : (
                                            isLoadingFeature ? <CircularProgress size={16} /> : 'N/A'
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {featureDetails?.testCasesCount !== undefined ? (
                                            featureDetails.testCasesCount > 0 ? (
                                              <MuiLink
                                                component="button"
                                                variant="body2"
                                                onClick={() => handleOpenListDialog('test-cases', feature.adoId, featureDetails?.title)}
                                                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                              >
                                                {featureDetails.testCasesCount}
                                              </MuiLink>
                                            ) : (
                                              <Typography variant="body2">{featureDetails.testCasesCount}</Typography>
                                            )
                                          ) : (
                                            isLoadingFeature ? <CircularProgress size={16} /> : 'N/A'
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <MuiLink
                                            component="button"
                                            variant="body2"
                                            onClick={() => openViewCommentsDialog(feature.adoId, featureDetails?.title)}
                                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                          >
                                            Comments
                                          </MuiLink>
                                        </TableCell>
                                        <TableCell>
                                          <Box display="flex" gap={0.5}>
                                            {canAddItems && (
                                              <Tooltip title="Refresh from ADO">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleRefreshRow(feature.adoId)}
                                                  disabled={refreshingAdoId === feature.adoId}
                                                >
                                                  {refreshingAdoId === feature.adoId ? (
                                                    <CircularProgress size={16} />
                                                  ) : (
                                                    <RefreshIcon fontSize="small" />
                                                  )}
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            {canAddItems && (
                                              <Tooltip title="Add to Weekly Report">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleMoveToWeeklyReport(feature.adoId)}
                                                  disabled={featureInWeeklyReport || movingToReportAdoId === feature.adoId}
                                                >
                                                  {movingToReportAdoId === feature.adoId ? (
                                                    <CircularProgress size={18} />
                                                  ) : (
                                                    <AddIcon fontSize="small" />
                                                  )}
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            <Tooltip title="Add comment">
                                              <IconButton
                                                size="small"
                                                onClick={() => openAddCommentDialog(feature.adoId, featureDetails?.title)}
                                              >
                                                <CommentIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add ADO Item Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { minWidth: 400 } }}>
        <DialogTitle sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Add ADO Item to WSR</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="ADO ID"
            type="text"
            fullWidth
            variant="outlined"
            value={newAdoId}
            onChange={(e) => setNewAdoId(e.target.value)}
            placeholder="Enter ADO work item ID"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddItem();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={!newAdoId.trim() || adding}
          >
            {adding ? <CircularProgress size={24} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bugs / Test Cases list popup */}
      <Dialog
        open={listDialogOpen}
        onClose={handleCloseListDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minHeight: 300 } }}
      >
        <DialogTitle sx={{ whiteSpace: 'normal', wordBreak: 'break-word', pr: 6 }}>
          {listDialogType === 'bugs' ? 'Bugs' : 'Test Cases'} for {listDialogTitle}
        </DialogTitle>
        <DialogContent>
          {listDialogLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
          ) : listDialogItems.length === 0 ? (
            <Typography color="text.secondary">No items found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80 }}>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell sx={{ width: 80 }}>State</TableCell>
                    {listDialogType === 'bugs' && <TableCell sx={{ width: 90 }}>Source</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listDialogItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <MuiLink
                          href={getAdoUrl(item.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          {item.id}
                          <OpenInNewIcon fontSize="small" />
                        </MuiLink>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 320 }}>
                        <Typography variant="body2">
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.state}</TableCell>
                      {listDialogType === 'bugs' && <TableCell>{item.source || '—'}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseListDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Comment Dialog - 5 sections with Not Applicable */}
      <Dialog
        open={addCommentDialogOpen}
        onClose={closeAddCommentDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minWidth: 720, minHeight: 720, maxHeight: '90vh' } }}
      >
        <DialogTitle>Add comment — {addCommentAdoId}</DialogTitle>
        <DialogContent sx={{ overflowY: 'auto' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A timestamp will be added automatically when you save. Fill in each section or mark as Not Applicable.
          </Typography>
          {WSR_COMMENT_SECTION_KEYS.map((key) => (
            <Box key={key} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle2" component="span">
                  {WSR_COMMENT_SECTION_LABELS[key]}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" component="label" sx={{ mr: 0.5 }}>
                    Not Applicable
                  </Typography>
                  <Checkbox
                    size="small"
                    checked={addCommentSections[key]?.notApplicable ?? false}
                    onChange={(e) =>
                      setAddCommentSections((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], notApplicable: e.target.checked }
                      }))
                    }
                    aria-label={`Not Applicable for ${WSR_COMMENT_SECTION_LABELS[key]}`}
                  />
                </Box>
              </Box>
              <TextField
                margin="dense"
                multiline
                minRows={3}
                maxRows={8}
                fullWidth
                variant="outlined"
                size="small"
                value={addCommentSections[key]?.text ?? ''}
                onChange={(e) =>
                  setAddCommentSections((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], text: e.target.value }
                  }))
                }
                placeholder={addCommentSections[key]?.notApplicable ? '' : 'Enter text...'}
                inputProps={{ readOnly: addCommentSections[key]?.notApplicable ?? false }}
                sx={{
                  ...((addCommentSections[key]?.notApplicable ?? false) && {
                    '& .MuiInputBase-input': { bgcolor: 'action.hover', cursor: 'not-allowed' }
                  })
                }}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddCommentDialog}>Cancel</Button>
          <Button
            onClick={handleAddCommentFromDialog}
            variant="contained"
            disabled={!hasCommentSectionsContent(addCommentSections) || !!submittingCommentAdoId}
          >
            {submittingCommentAdoId ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Comments Dialog */}
      <Dialog
        open={viewCommentsDialogOpen}
        onClose={closeViewCommentsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minWidth: 520, minHeight: 320 } }}
      >
        <DialogTitle>Comments — {viewCommentsAdoId}</DialogTitle>
        <DialogContent>
          {viewCommentsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
          ) : viewCommentsList.length === 0 ? (
            <Typography color="text.secondary">No comments yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {viewCommentsList.map((c) => (
                <Paper key={c._id || c.timestamp} variant="outlined" sx={{ p: 1.5 }}>
                  {renderCommentBody(c)}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {c.user} — {formatDateTime(c.timestamp)}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewCommentsDialog}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<CommentIcon />}
            onClick={() => {
              if (viewCommentsAdoId) {
                closeViewCommentsDialog();
                openAddCommentDialog(viewCommentsAdoId, viewCommentsTitle);
              }
            }}
          >
            Add comment
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default WSRView;
